import { isScmQuoteOnlyTitle } from "./scm-quote-only";
import { updateElements } from "./updateElements";

type CartItemProperties = Record<string, string | null>;

type CartItem = {
  key: string;
  variant_id: number;
  quantity: number;
  properties?: CartItemProperties | null;
};

type CartResponse = {
  items?: CartItem[];
};

const CART_FETCH_TIMEOUT_MS = 8000;
const WISHLIST_ITEM_SELECTOR = ".swym-wishlist-item";
const SKU_PROPERTY_LABEL_FALLBACK = "Product Code";
const SPECIAL_ORDER_TAG_LABEL = "Special Order";
const SPECIAL_ORDER_PROPERTY_KEY = "_special_order";

const getSkuPropertyLabel = (): string => {
  const container = document.getElementById("swym-wishlist-render-container");
  if (!(container instanceof HTMLElement)) return SKU_PROPERTY_LABEL_FALLBACK;

  const fromDataset = container.dataset.labelSku?.trim() ?? "";

  return fromDataset || SKU_PROPERTY_LABEL_FALLBACK;
};

const fetchJson = <T>(url: string): Promise<T | null> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), CART_FETCH_TIMEOUT_MS);

  return fetch(url, { signal: controller.signal })
    .then((response) => (response.ok ? response.json() : null))
    .catch(() => null)
    .finally(() => clearTimeout(timeoutId));
};

const updateCartItemSkuProperty = async (
  wishlistItemElement: Element | null,
): Promise<boolean> => {
  if (!(wishlistItemElement instanceof HTMLElement)) return false;

  const rawVariantId = wishlistItemElement.dataset.variantId?.trim() ?? "";
  const sku = wishlistItemElement.dataset.sku?.trim() ?? "";

  if (!rawVariantId || !sku) return false;

  const numericVariantId = Number(rawVariantId);
  if (!Number.isFinite(numericVariantId)) return false;

  const cart = await fetchJson<CartResponse>("/cart.js");
  const items = cart?.items;
  if (!items?.length) return false;

  const targetItems = items.filter(
    (item) => item.variant_id === numericVariantId,
  );
  if (!targetItems.length) return false;

  const specialOrderValue =
    await getSpecialOrderPropertyValue(wishlistItemElement);

  const skuPropertyLabel = getSkuPropertyLabel();
  const updatePayloads = targetItems.reduce<
    {
      id: string;
      quantity: number;
      properties: Record<string, string>;
    }[]
  >((accumulator, targetItem) => {
    const existingProperties = targetItem.properties;

    const currentSkuRaw =
      existingProperties && existingProperties[skuPropertyLabel]
        ? existingProperties[skuPropertyLabel]
        : null;
    const currentSku =
      typeof currentSkuRaw === "string" ? currentSkuRaw.trim() : "";

    if (currentSku === sku) {
      return accumulator;
    }

    const properties: Record<string, string> = {};

    if (existingProperties) {
      for (const key in existingProperties) {
        if (!Object.prototype.hasOwnProperty.call(existingProperties, key)) {
          continue;
        }
        if (key === "SKU" || key === skuPropertyLabel) {
          continue;
        }

        const value = existingProperties[key];
        if (typeof value === "string" && value.trim()) {
          properties[key] = value;
        }
      }
    }

    properties[skuPropertyLabel] = sku;

    if (specialOrderValue) {
      properties[SPECIAL_ORDER_PROPERTY_KEY] = specialOrderValue;
    }

    accumulator.push({
      id: targetItem.key,
      quantity: targetItem.quantity,
      properties,
    });

    return accumulator;
  }, []);

  if (!updatePayloads.length) {
    return false;
  }

  try {
    await Promise.all(
      updatePayloads.map((payload) =>
        fetch("/cart/change.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        }),
      ),
    );
    return true;
  } catch {
    // eslint-disable-next-line no-console
    console.error("Failed to update cart item properties");
  }

  return false;
};

type QfcAvailabilityResponse = {
  isVariantAvailable: boolean;
};

const getSoldOutLabel = () => {
  const body = document.body;
  if (!body) {
    return "";
  }

  const { labelSoldOut } = body.dataset;

  return labelSoldOut ?? "";
};

const disableQfcUnavailableButton = (button: HTMLButtonElement) => {
  if (button.dataset.qfcAvailability === "unavailable") {
    return;
  }

  button.disabled = true;
  button.dataset.qfcAvailability = "unavailable";
  button.setAttribute("aria-disabled", "true");

  const soldOutLabel = getSoldOutLabel();

  if (soldOutLabel.length > 0) {
    const applyLabel = () => {
      if (button.textContent !== soldOutLabel) {
        button.textContent = soldOutLabel;
      }

      button.setAttribute("aria-label", soldOutLabel);
    };

    applyLabel();

    const labelObserver = new MutationObserver(() => {
      if (button.dataset.qfcAvailability !== "unavailable") {
        labelObserver.disconnect();
        return;
      }

      applyLabel();
    });

    labelObserver.observe(button, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  }
};

const getQfcAvailabilityUrl = (button: HTMLButtonElement) => {
  const wishlistItem = button.closest(WISHLIST_ITEM_SELECTOR);
  const detailContainer = button.closest(
    ".swym-wishlist-product-detail-text-container",
  );

  const root = wishlistItem ?? detailContainer;

  if (!root) {
    return "";
  }

  const link =
    root.querySelector<HTMLAnchorElement>(".swym-wishlist-image-wrapper") ??
    root.querySelector<HTMLAnchorElement>(".swym-more-details-btn");

  if (!link) {
    return "";
  }

  const href = link.getAttribute("href");

  if (!href) {
    return "";
  }

  let productUrl: URL;

  try {
    productUrl = href.startsWith("http")
      ? new URL(href)
      : new URL(href, window.location.origin);
  } catch (_error) {
    return "";
  }

  const variantId = productUrl.searchParams.get("variant");

  if (!variantId) {
    return "";
  }

  const availabilityUrl = new URL(
    productUrl.pathname,
    productUrl.origin || window.location.origin,
  );

  availabilityUrl.searchParams.set("variant", variantId);
  availabilityUrl.searchParams.set("view", "qfc-availability");

  return availabilityUrl.toString();
};

const initializedButtons = new WeakSet<HTMLButtonElement>();

const updateWishlistButtonAvailability = async (button: HTMLButtonElement) => {
  if (button.dataset.qfcAvailability === "unavailable") {
    return;
  }

  const availabilityUrl = getQfcAvailabilityUrl(button);

  if (!availabilityUrl) {
    return;
  }

  try {
    const response = await fetch(availabilityUrl);

    if (!response.ok) {
      return;
    }

    const availability: QfcAvailabilityResponse = await response.json();

    if (!availability.isVariantAvailable) {
      disableQfcUnavailableButton(button);
    }
  } catch (_error) {
    // eslint-disable-next-line no-console
    console.error("QFC availability check failed");
  }
};

type SchoolQuoteConfig = {
  url: string;
  label: string;
  tag: string;
};

type ProductJson = {
  tags?: string[];
  title?: string;
};

const getSpecialOrderPropertyValue = async (
  wishlistItemElement: HTMLElement,
): Promise<string | null> => {
  const cached = wishlistItemElement.dataset.specialOrder;
  if (cached === "true") return "true";
  if (cached === "false") return null;

  const handle = extractHandleFromContext(wishlistItemElement);
  if (!handle) {
    wishlistItemElement.dataset.specialOrder = "false";
    return null;
  }

  const product = await fetchJson<ProductJson>(
    `/products/${encodeURIComponent(handle)}.js`,
  );
  const tags = product?.tags;
  if (!tags || !tags.length) {
    wishlistItemElement.dataset.specialOrder = "false";
    return null;
  }

  const expectedTag = normalizeTag(SPECIAL_ORDER_TAG_LABEL);
  const hasSpecialOrder = tags.some(
    (tag) => !!tag && normalizeTag(tag) === expectedTag,
  );

  wishlistItemElement.dataset.specialOrder = hasSpecialOrder ? "true" : "false";

  return hasSpecialOrder ? "true" : null;
};

declare global {
  interface Window {
    carbatecSchoolQuote?: SchoolQuoteConfig;
  }
}

const SCHOOL_TAG_FALLBACK = "school";
const WISHLIST_ADD_TO_CART_BUTTON_SELECTOR =
  ".swym-add-to-cart-btn, .swym-wishlist-add-to-cart-btn";
const WISHLIST_IMAGE_LINK_SELECTOR = "a.swym-wishlist-image-wrapper";
const WISHLIST_DETAIL_SELECTOR = ".swym-wishlist-product-detail-content";
const WISHLIST_DETAIL_LINK_SELECTOR = ".swym-more-details-btn.swym-is-anchor";

const getQuoteConfig = (): SchoolQuoteConfig | null => {
  const config = window.carbatecSchoolQuote;
  if (!config || !config.url || !config.label) return null;
  return {
    url: config.url,
    label: config.label,
    tag: config.tag || SCHOOL_TAG_FALLBACK,
  };
};

const normalizeTag = (value: string): string => value.trim().toLowerCase();

const hasSchoolTag = (tags: string[], schoolTag: string): boolean => {
  const expected = normalizeTag(schoolTag || SCHOOL_TAG_FALLBACK);
  return tags.some((tag) => tag && normalizeTag(tag) === expected);
};

const extractHandleFromHref = (href: string): string | null => {
  try {
    const url = new URL(href, window.location.origin);
    const segments = url.pathname.split("/");
    const productsIndex = segments.indexOf("products");
    if (productsIndex === -1) return null;
    const handle = segments[productsIndex + 1] || "";
    return handle || null;
  } catch {
    return null;
  }
};

const extractHandleFromContext = (context: HTMLElement): string | null => {
  const imageLink = context.querySelector(WISHLIST_IMAGE_LINK_SELECTOR);
  const detailsLink = context.querySelector(WISHLIST_DETAIL_LINK_SELECTOR);
  const link =
    imageLink instanceof HTMLAnchorElement
      ? imageLink
      : detailsLink instanceof HTMLAnchorElement
        ? detailsLink
        : null;
  if (!link) return null;
  return extractHandleFromHref(link.href);
};

const attachSchoolLabelObserver = (
  button: HTMLElement,
  label: string,
): void => {
  if (button.dataset.schoolLabelBound === "true") return;
  button.dataset.schoolLabelBound = "true";
  button.dataset.schoolLabel = label;

  const observer = new MutationObserver(() => {
    const expectedLabel = button.dataset.schoolLabel;
    if (!expectedLabel) return;
    if (button.textContent === expectedLabel) return;
    button.textContent = expectedLabel;
  });

  observer.observe(button, {
    childList: true,
    characterData: true,
    subtree: true,
  });
};

const disableScmQuoteButton = (button: HTMLButtonElement): void => {
  if (button.dataset.scmQuoteOnly === "true") return;

  button.disabled = true;
  button.dataset.scmQuoteOnly = "true";
  button.setAttribute("aria-disabled", "true");

  const soldOutLabel = getSoldOutLabel();
  if (!soldOutLabel.length) return;

  const applyLabel = () => {
    if (button.textContent !== soldOutLabel) {
      button.textContent = soldOutLabel;
    }
    button.setAttribute("aria-label", soldOutLabel);
  };

  applyLabel();

  const labelObserver = new MutationObserver(() => {
    if (button.dataset.scmQuoteOnly !== "true") {
      labelObserver.disconnect();
      return;
    }
    applyLabel();
  });

  labelObserver.observe(button, {
    childList: true,
    characterData: true,
    subtree: true,
  });
};

const applySchoolQuoteButton = (
  button: HTMLButtonElement,
  config: SchoolQuoteConfig,
): void => {
  button.dataset.schoolProduct = "true";
  button.textContent = config.label;
  button.setAttribute("aria-label", config.label);
  attachSchoolLabelObserver(button, config.label);
};

const applyQuoteRules = (
  button: HTMLButtonElement,
  quoteConfig: SchoolQuoteConfig | null,
): void => {
  const gridItem = button.closest(WISHLIST_ITEM_SELECTOR);
  const detailItem = button.closest(WISHLIST_DETAIL_SELECTOR);
  const contextElement = gridItem ?? detailItem;
  if (!(contextElement instanceof HTMLElement)) return;

  if (contextElement.dataset.quoteRulesChecked === "true") {
    if (quoteConfig && contextElement.dataset.schoolProduct === "true") {
      applySchoolQuoteButton(button, quoteConfig);
    }
    if (contextElement.dataset.scmQuoteOnly === "true") {
      disableScmQuoteButton(button);
    }
    return;
  }

  const handle = extractHandleFromContext(contextElement);
  if (!handle) {
    contextElement.dataset.quoteRulesChecked = "true";
    contextElement.dataset.schoolProduct = "false";
    contextElement.dataset.scmQuoteOnly = "false";
    return;
  }

  void fetch(`/products/${encodeURIComponent(handle)}.js`)
    .then((response) => (response.ok ? response.json() : null))
    .then((data: ProductJson | null) => {
      contextElement.dataset.quoteRulesChecked = "true";

      const productTags = Array.isArray(data?.tags) ? data.tags : null;
      const isSchoolProduct =
        Boolean(quoteConfig) &&
        Array.isArray(productTags) &&
        hasSchoolTag(productTags, quoteConfig?.tag ?? "");
      contextElement.dataset.schoolProduct = isSchoolProduct ? "true" : "false";
      if (isSchoolProduct && quoteConfig) {
        applySchoolQuoteButton(button, quoteConfig);
      }

      const isScmQuoteOnly = Boolean(data && isScmQuoteOnlyTitle(data.title));
      contextElement.dataset.scmQuoteOnly = isScmQuoteOnly ? "true" : "false";
      if (isScmQuoteOnly) {
        disableScmQuoteButton(button);
      }
    })
    .catch(() => {
      contextElement.dataset.quoteRulesChecked = "true";
      contextElement.dataset.schoolProduct = "false";
      contextElement.dataset.scmQuoteOnly = "false";
    });
};

const handleButtonClick = (button: HTMLElement) => {
  if (button.dataset.schoolProduct === "true") return;
  if (button.dataset.scmQuoteOnly === "true") return;

  const wishlistItemElement = button.closest(WISHLIST_ITEM_SELECTOR);

  type CartDrawerElement = Element & {
    open: (triggeredBy?: Element) => void;
  };

  const isCartDrawerElement = (
    element: Element | null,
  ): element is CartDrawerElement => {
    return !!element && "open" in element;
  };

  const syncSkuAndCartDrawer = (delays: number[]): void => {
    if (!delays.length) return;

    const [delay, ...nextDelays] = delays;

    window.setTimeout(() => {
      void (async () => {
        const didUpdate = await updateCartItemSkuProperty(wishlistItemElement);

        if (!didUpdate) {
          syncSkuAndCartDrawer(nextDelays);
          return;
        }

        const cartDrawer = document.querySelector("cart-drawer");
        if (!isCartDrawerElement(cartDrawer)) return;

        await updateElements([".drawer__inner", ".cart-count-bubble"]);
        cartDrawer.classList.remove("is-empty");
        cartDrawer.open();
      })();
    }, delay);
  };

  syncSkuAndCartDrawer([0, 400, 1200, 3000]);
};

const findAndHandleButtons = (): void => {
  const buttons: NodeListOf<HTMLButtonElement> = document.querySelectorAll(
    WISHLIST_ADD_TO_CART_BUTTON_SELECTOR,
  );
  if (!buttons.length) return;

  const quoteConfig = getQuoteConfig();

  buttons.forEach((button) => {
    applyQuoteRules(button, quoteConfig);
  });
};

const handleWishlistAddToCart = () => {
  const applyToButtons = () => {
    const buttons = document.querySelectorAll<HTMLButtonElement>(
      WISHLIST_ADD_TO_CART_BUTTON_SELECTOR,
    );

    buttons.forEach((button) => {
      if (initializedButtons.has(button)) {
        return;
      }

      initializedButtons.add(button);

      button.addEventListener("click", () => handleButtonClick(button));
      void updateWishlistButtonAvailability(button);
    });
  };

  const mainObserver = new MutationObserver(() => {
    applyToButtons();
    findAndHandleButtons();
  });

  mainObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });

  applyToButtons();
  findAndHandleButtons();
};

const handleWishlistQuoteClick = (event: MouseEvent): void => {
  const quoteConfig = getQuoteConfig();
  if (!quoteConfig) return;

  const target = event.target;
  if (!(target instanceof Element)) return;

  const button = target.closest(WISHLIST_ADD_TO_CART_BUTTON_SELECTOR);
  if (!(button instanceof HTMLButtonElement)) return;
  if (button.dataset.schoolProduct !== "true") return;

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  window.location.href = quoteConfig.url;
};

const initWishlistHandlers = () => {
  handleWishlistAddToCart();
  document.addEventListener("click", handleWishlistQuoteClick, true);
};

initWishlistHandlers();
